import React, { useState, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Search, X, Grid3X3, Loader2, Star, ChevronRight, Music, FileText, Package, ArrowDown, Guitar, Download } from 'lucide-react';
import Layout from '../components/Layout';
import ProductCard from '../components/ProductCard';
import SEO from '../components/SEO';
import { categories } from '../data/products';
import { useSanityProducts } from '../hooks/useSanityProducts';
import { useTranslation } from '../context/TranslationContext';
import { sampleCopy } from '../data/sampleFiles';
import { useSampleFiles } from '../hooks/useSampleFiles';
import { isIndexedBook, bookPlatform, bookInstrument } from '../utils/bookGrouping';

// Best seller slugs (owner-defined priority)
var BEST_SELLER_SLUGS = ['noteturner', 'muteclip', 'belloflex-mute', 'brass-plank'];

// Instrument-specific product slugs
var INSTRUMENT_PRODUCTS = {
  trumpet: ['noteturner', 'muteclip', 'belloflex-mute', 'brass-plank', 'fingerflow', 'practice-mute', 'mutecap', 'mutemute', 'piccolo-mute', 'nimbus-bucket-mute', 'cork-harmon-mute', 'soniclift', 'brassgutter', 'brassblast', 'trumpet-o-rings', 'water-key-tip-cover', 'neocap-pouch', 'pencil-holder', 'glow-cap-km-stand', 'mouthpiece-holder', 'slide-rubber-bands', 'buzztone', 'yamaha-personal-studio', 'trumpet-visualizer', 'ganschtrigger', 'armis-bellguard', 'armis-leather-trumpet-sleeve', 'trumpet-saddle', 'gl-trumpet-case', 'yamaha-double-case', 'p-mauriat-pmt75', 'bach-strad-229c', 'staff-writter', 'real-book-vol-1-bb', 'real-book-vol-2-bb', 'real-book-vol-3-bb', 'new-real-book-vol-1-bb', 'new-real-book-vol-2-bb', 'new-real-book-vol-3-bb', 'arabesque-no-1', 'art-in-trumpet-album', 'blue-rondo-a-la-turk', 'campanella', 'caprice-24', 'chanson-arabe', 'danse-espagnol', 'danse-macabre', 'espada-de-la-noche', 'gliere-horn-concerto', 'green-hornet-acapella', 'green-hornet-big-band', 'hora-staccato', 'i-cant-get-started', 'le-coucou', 'le-plus-que-lente', 'les-chemins-de-lamour', 'libertango', 'malaguena-solo', 'meditation', 'mendelssohn-violin-concerto', 'oblivion', 'pavane-pour-une-infante-defunte', 'romanian-folk-dances', 'tuning-chart'],
  saxophone: ['saxolift', 'noteturner', 'brass-plank', 'real-book-vol-1-bb', 'real-book-vol-2-bb', 'real-book-vol-3-bb', 'new-real-book-vol-1-bb', 'new-real-book-vol-2-bb', 'new-real-book-vol-3-bb', 'real-book-vol-1-eb', 'real-book-vol-2-eb', 'new-real-book-vol-1-eb', 'new-real-book-vol-2-eb', 'new-real-book-vol-3-eb'],
  trombone: ['noteturner', 'brass-plank', 'trombuddy', 'real-book-vol-1-bass', 'real-book-vol-2-bass', 'real-book-vol-3-bass', 'staff-writter'],
  piano: ['noteturner', 'real-book-vol-1-c', 'real-book-vol-2-c', 'real-book-vol-3-c', 'new-real-book-vol-1-c', 'new-real-book-vol-2-c', 'new-real-book-vol-3-c', 'real-christmas-book-c']
};

// Indexed digital books (forScore .4sc + MobileSheets .msf editions)
function isForscoreProduct(p) {
  return isIndexedBook(p);
}

// Arrangement = sheet-music category products that are NOT Real Books
function isArrangement(p) {
  return p.category === 'sheet-music' && !isForscoreProduct(p);
}

// Physical = everything NOT sheet-music
function isPhysicalProduct(p) {
  return p.category !== 'sheet-music';
}

// Section header component
function SectionHeader({ title, subtitle, count, testId }) {
  return (
    <div className="mb-8 mt-16 first:mt-0" data-testid={testId}>
      <div className="flex items-center gap-4 mb-2">
        <h2 className="text-2xl md:text-3xl font-bold text-white">{title}</h2>
        {count !== undefined && (
          <span className="px-3 py-1 rounded-full bg-neutral-800 text-neutral-400 text-sm">
            {count}
          </span>
        )}
      </div>
      {subtitle && (
        <p className="text-neutral-500 text-sm">{subtitle}</p>
      )}
      <div className="mt-4 h-px bg-gradient-to-r from-[#d4af37]/40 via-[#d4af37]/20 to-transparent" />
    </div>
  );
}

// Subsection header for nested categories within digital
function SubSectionHeader({ title, count, testId }) {
  return (
    <div className="mb-6 mt-10 first:mt-0" data-testid={testId}>
      <div className="flex items-center gap-3">
        <ChevronRight className="w-5 h-5 text-[#d4af37]" />
        <h3 className="text-lg md:text-xl font-semibold text-white">{title}</h3>
        {count !== undefined && (
          <span className="px-2.5 py-0.5 rounded-full bg-neutral-800/80 text-neutral-500 text-xs">
            {count}
          </span>
        )}
      </div>
    </div>
  );
}

function ProductGrid({ products }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
      {products.map(function(product, index) {
        return <ProductCard key={product.id || product._id || product.slug} product={product} index={index} />;
      })}
    </div>
  );
}

function ShopPage() {
  var { t, language } = useTranslation();
  var sampleUrlFor = useSampleFiles();
  var [selectedCategory, setSelectedCategory] = useState('all');
  var [searchQuery, setSearchQuery] = useState('');
  var [sortBy, setSortBy] = useState('best-sellers');
  var [instrumentFilter, setInstrumentFilter] = useState('all');

  // Refs for section scrolling
  var bestSellersRef = useRef(null);
  var physicalRef = useRef(null);
  var digitalRef = useRef(null);
  var arrangementsRef = useRef(null);
  var forscoreRef = useRef(null);
  var mobilesheetsRef = useRef(null);

  function scrollToSection(ref) {
    if (ref.current) {
      // Reset filters so sectioned view is shown
      setSelectedCategory('all');
      setSearchQuery('');
      setTimeout(function() {
        ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }

  // Fetch products from Sanity
  var { products, loading: isLoading, error: sanityError } = useSanityProducts({
    category: selectedCategory === 'all' ? null : selectedCategory
  });

  // Is the user actively filtering/searching?
  var isFiltered = selectedCategory !== 'all' || searchQuery.length > 0 || instrumentFilter !== 'all';

  // Flat filtered list (used when filters are active)
  var filteredProducts = useMemo(function() {
    var filtered = products.slice();

    if (instrumentFilter !== 'all') {
      var allowedSlugs = INSTRUMENT_PRODUCTS[instrumentFilter] || [];
      filtered = filtered.filter(function(p) {
        if (allowedSlugs.indexOf(p.slug) !== -1) return true;
        // Digital books follow their key: C = piano/guitar, Bb = trumpet,
        // Eb = alto sax, bass clef = trombone (override per product in admin).
        return isIndexedBook(p) && bookInstrument(p) === instrumentFilter;
      });
    }

    if (searchQuery) {
      var query = searchQuery.toLowerCase();
      filtered = filtered.filter(function(p) {
        return p.name.toLowerCase().includes(query) ||
          (p.description && p.description.toLowerCase().includes(query));
      });
    }

    if (sortBy === 'price-low') {
      filtered.sort(function(a, b) { return a.price - b.price; });
    } else if (sortBy === 'price-high') {
      filtered.sort(function(a, b) { return b.price - a.price; });
    } else if (sortBy === 'name') {
      filtered.sort(function(a, b) { return a.name.localeCompare(b.name); });
    } else if (sortBy === 'best-sellers') {
      var prioritySlugs = BEST_SELLER_SLUGS;
      filtered.sort(function(a, b) {
        var aIdx = prioritySlugs.indexOf(a.slug);
        var bIdx = prioritySlugs.indexOf(b.slug);
        if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
        if (aIdx !== -1) return -1;
        if (bIdx !== -1) return 1;
        var aNew = a.badge === 'NEW' ? 1 : 0;
        var bNew = b.badge === 'NEW' ? 1 : 0;
        if (aNew !== bNew) return bNew - aNew;
        return a.name.localeCompare(b.name);
      });
    } else {
      filtered.sort(function(a, b) { return (b.featured ? 1 : 0) - (a.featured ? 1 : 0); });
    }

    return filtered;
  }, [selectedCategory, searchQuery, sortBy, products, instrumentFilter]);

  // Sectioned data (used in default "all" view with no search)
  var sections = useMemo(function() {
    if (isFiltered) return null;

    var bestSellers = [];
    var physical = [];
    var arrangements = [];
    var forscore = [];
    var mobilesheets = [];

    products.forEach(function(p) {
      // Check best seller
      if (BEST_SELLER_SLUGS.includes(p.slug)) {
        bestSellers.push(p);
      }

      // Categorize
      if (isForscoreProduct(p)) {
        if (bookPlatform(p) === 'mobilesheets') mobilesheets.push(p);
        else forscore.push(p);
      } else if (isArrangement(p)) {
        arrangements.push(p);
      } else if (isPhysicalProduct(p)) {
        physical.push(p);
      }
    });

    // Sort best sellers in priority order
    bestSellers.sort(function(a, b) {
      return BEST_SELLER_SLUGS.indexOf(a.slug) - BEST_SELLER_SLUGS.indexOf(b.slug);
    });

    // Sort physical: NEW items first, then by name
    physical.sort(function(a, b) {
      var aNew = a.badge === 'NEW' ? 1 : 0;
      var bNew = b.badge === 'NEW' ? 1 : 0;
      if (aNew !== bNew) return bNew - aNew;
      return a.name.localeCompare(b.name);
    });

    // Sort arrangements and forscore by name
    arrangements.sort(function(a, b) { return a.name.localeCompare(b.name); });
    forscore.sort(function(a, b) { return a.name.localeCompare(b.name); });
    mobilesheets.sort(function(a, b) { return a.name.localeCompare(b.name); });

    return { bestSellers: bestSellers, physical: physical, arrangements: arrangements, forscore: forscore, mobilesheets: mobilesheets };
  }, [products, isFiltered]);

  var categoryCount = useMemo(function() {
    var counts = { all: products.length };
    products.forEach(function(p) {
      counts[p.category] = (counts[p.category] || 0) + 1;
    });
    return counts;
  }, [products]);

  var categoryNames = {
    all: t('allProducts'),
    accessories: t('accessories'),
    mutes: t('mutes'),
    'sheet-music': t('sheetMusic'),
    instruments: t('instruments'),
  };

  return (
    <Layout>
      <SEO 
        title="Shop Trumpet Accessories, Mutes & Sheet Music"
        description="Shop NoteTurner, MuteClip, practice mutes, Real Books indexed for ForScore & professional trumpet arrangements. Free shipping on orders over $50."
        url="/shop"
      />
      {/* Hero */}
      <section className="pt-32 pb-20 bg-gradient-to-b from-neutral-900 to-black">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <p className="text-sm tracking-widest uppercase text-[#d4af37] mb-4">{t('shop')}</p>
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6">
            {t('professionalGear')}
          </h1>
          <p className="text-xl text-neutral-400 max-w-2xl mx-auto">
            {t('shopDescription')}
          </p>
        </div>
      </section>

      {/* Instrument Filter */}
      <section className="bg-black border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-4">
          <div className="flex flex-wrap justify-center gap-2">
            {[
              { key: 'all', i18n: 'allInstruments', label: 'All Instruments' },
              { key: 'trumpet', i18n: 'trumpetFrenchHorn', label: 'Trumpet / French Horn' },
              { key: 'saxophone', i18n: 'saxophoneClarinet', label: 'Saxophone / Clarinet' },
              { key: 'trombone', i18n: 'tromboneBass', label: 'Trombone / Bass' },
              { key: 'piano', i18n: 'pianoGuitar', label: 'Piano / Guitar' }
            ].map(function(inst) {
              var isActive = instrumentFilter === inst.key;
              return (
                <button
                  key={inst.key}
                  data-testid={'instrument-filter-' + inst.key}
                  onClick={function() { setInstrumentFilter(inst.key); }}
                  className={'px-4 py-2 rounded-full text-sm font-medium transition-all ' +
                    (isActive
                      ? 'bg-[#d4af37] text-black'
                      : 'bg-neutral-900/50 text-neutral-400 hover:text-white border border-white/5 hover:border-white/20')}
                >
                  {t(inst.i18n) || inst.label}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Quick Navigation */}
      <section className="bg-black border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-6">
          <div className="flex flex-wrap justify-center gap-3">
            <button
              onClick={function() { scrollToSection(bestSellersRef); }}
              data-testid="jump-best-sellers"
              className="flex items-center gap-2 px-5 py-3 rounded-xl bg-gradient-to-r from-[#d4af37]/20 to-[#d4af37]/5 border border-[#d4af37]/30 text-[#d4af37] text-sm font-medium hover:from-[#d4af37]/30 hover:to-[#d4af37]/10 transition-all"
            >
              <Star className="w-4 h-4" />
              {t('bestSellers') || 'Best Sellers'}
            </button>
            <button
              onClick={function() { scrollToSection(physicalRef); }}
              data-testid="jump-physical"
              className="flex items-center gap-2 px-5 py-3 rounded-xl bg-neutral-900 border border-white/10 text-neutral-300 text-sm font-medium hover:border-white/20 hover:text-white transition-all"
            >
              <Package className="w-4 h-4" />
              {t('physicalProducts') || 'Physical Products'}
            </button>
            <button
              onClick={function() { scrollToSection(arrangementsRef); }}
              data-testid="jump-arrangements"
              className="flex items-center gap-2 px-5 py-3 rounded-xl bg-neutral-900 border border-white/10 text-neutral-300 text-sm font-medium hover:border-[#d4af37]/30 hover:text-[#d4af37] transition-all"
            >
              <Music className="w-4 h-4" />
              {t('arrangements') || 'Arrangements'}
            </button>
            <button
              onClick={function() { scrollToSection(forscoreRef); }}
              data-testid="jump-forscore"
              className="flex items-center gap-2 px-5 py-3 rounded-xl bg-neutral-900 border border-sky-500/30 text-sky-400 text-sm font-medium hover:border-sky-400/60 hover:text-sky-300 transition-all"
            >
              <FileText className="w-4 h-4" />
              {t('forscoreFiles') || 'forScore Indexed Files'}
            </button>
            <button
              onClick={function() { scrollToSection(mobilesheetsRef); }}
              data-testid="jump-mobilesheets"
              className="flex items-center gap-2 px-5 py-3 rounded-xl bg-neutral-900 border border-emerald-500/30 text-emerald-400 text-sm font-medium hover:border-emerald-400/60 hover:text-emerald-300 transition-all"
            >
              <FileText className="w-4 h-4" />
              MobileSheets Indexed Files
            </button>
          </div>
        </div>
      </section>

      {/* Shop Content */}
      <section data-testid="shop-section" className="py-12 bg-black">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          {/* Filters Bar */}
          <div className="flex flex-col lg:flex-row gap-6 mb-12">
            {/* Categories - Horizontal Scroll */}
            <div className="flex-1 overflow-x-auto">
              <div className="flex gap-2 pb-2">
                {categories.map(function(cat) {
                  var isActive = selectedCategory === cat.id;
                  var i18nKey = cat.id === 'all' ? 'allProducts' : (cat.id === 'sheet-music' ? 'sheetMusic' : cat.id);
                  var label = t(i18nKey) || cat.name;
                  return (
                    <button
                      key={cat.id}
                      data-testid={'category-' + cat.id}
                      onClick={function() { setSelectedCategory(cat.id); }}
                      className={'flex-shrink-0 px-5 py-2.5 rounded-full text-sm font-medium transition-all ' +
                        (isActive
                          ? 'bg-[#d4af37] text-black'
                          : 'bg-neutral-900 text-neutral-400 hover:bg-neutral-800 hover:text-white')}
                    >
                      {label}
                      <span className="ml-2 text-xs opacity-60">({categoryCount[cat.id] || 0})</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Search & Sort */}
            <div className="flex gap-3">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={function(e) { setSearchQuery(e.target.value); }}
                  data-testid="shop-search-input"
                  className="w-48 pl-10 pr-4 py-2.5 bg-neutral-900 border border-white/5 rounded-full text-white text-sm placeholder:text-neutral-600 focus:outline-none focus:border-[#d4af37]/50"
                />
              </div>

              <select
                value={sortBy}
                onChange={function(e) { setSortBy(e.target.value); }}
                data-testid="shop-sort-select"
                className="px-4 py-2.5 bg-neutral-900 border border-white/5 rounded-full text-white text-sm focus:outline-none focus:border-[#d4af37]/50 cursor-pointer"
              >
                <option value="best-sellers">Best Sellers</option>
                <option value="featured">Featured</option>
                <option value="price-low">Price: Low-High</option>
                <option value="price-high">Price: High-Low</option>
                <option value="name">Name: A-Z</option>
              </select>
            </div>
          </div>

          {/* Active Filters */}
          {(selectedCategory !== 'all' || searchQuery || instrumentFilter !== 'all') && (
            <div className="flex flex-wrap gap-2 mb-8">
              {instrumentFilter !== 'all' && (
                <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#d4af37]/10 text-[#d4af37] text-sm rounded-full">
                  {instrumentFilter.charAt(0).toUpperCase() + instrumentFilter.slice(1)}
                  <button onClick={function() { setInstrumentFilter('all'); }}>
                    <X className="w-4 h-4" />
                  </button>
                </span>
              )}
              {selectedCategory !== 'all' && (
                <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#d4af37]/10 text-[#d4af37] text-sm rounded-full">
                  {categories.find(function(c) { return c.id === selectedCategory; })?.name}
                  <button onClick={function() { setSelectedCategory('all'); }}>
                    <X className="w-4 h-4" />
                  </button>
                </span>
              )}
              {searchQuery && (
                <span className="inline-flex items-center gap-2 px-4 py-2 bg-neutral-800 text-white text-sm rounded-full">
                  "{searchQuery}"
                  <button onClick={function() { setSearchQuery(''); }}>
                    <X className="w-4 h-4" />
                  </button>
                </span>
              )}
            </div>
          )}

          {/* Results Count */}
          <p className="text-sm text-neutral-600 mb-8" data-testid="results-count">
            {isLoading ? 'Loading...' : filteredProducts.length + ' product' + (filteredProducts.length !== 1 ? 's' : '')}
          </p>

          {/* Loading State */}
          {isLoading ? (
            <div className="text-center py-24">
              <Loader2 className="w-12 h-12 mx-auto mb-4 text-[#d4af37] animate-spin" />
              <p className="text-neutral-500">Loading products from Sanity...</p>
            </div>
          ) : isFiltered ? (
            /* Flat filtered view */
            filteredProducts.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {filteredProducts.map(function(product, index) {
                  return <ProductCard key={product.id || product._id || product.slug} product={product} index={index} />;
                })}
              </div>
            ) : (
              <div className="text-center py-24">
                <Grid3X3 className="w-16 h-16 mx-auto mb-4 text-neutral-800" />
                <p className="text-neutral-500 text-lg">No products found</p>
                <button
                  onClick={function() {
                    setSelectedCategory('all');
                    setSearchQuery('');
                    setInstrumentFilter('all');
                  }}
                  className="mt-4 text-[#d4af37] hover:underline"
                >
                  Clear all filters
                </button>
              </div>
            )
          ) : sections ? (
            /* Sectioned default view */
            <div>
              {/* Best Sellers */}
              {sections.bestSellers.length > 0 && (
                <div ref={bestSellersRef}>
                  <SectionHeader
                    title={t('bestSellers') || 'Best Sellers'}
                    subtitle={t('bestSellersSubtitle') || 'Our most popular products loved by musicians worldwide'}
                    count={sections.bestSellers.length}
                    testId="section-best-sellers"
                  />
                  <ProductGrid products={sections.bestSellers} />
                </div>
              )}

              {/* Physical Products */}
              {sections.physical.length > 0 && (
                <div ref={physicalRef}>
                  <SectionHeader
                    title={t('physicalProducts') || 'Physical Products'}
                    subtitle={t('physicalProductsSubtitle') || 'Accessories, mutes, instruments & more — shipped to your door'}
                    count={sections.physical.length}
                    testId="section-physical-products"
                  />
                  <ProductGrid products={sections.physical} />
                </div>
              )}

              {/* Digital Products */}
              {(sections.arrangements.length > 0 || sections.forscore.length > 0) && (
                <div ref={digitalRef}>
                  <SectionHeader
                    title={t('digitalProducts') || 'Digital Products'}
                    subtitle={t('digitalProductsSubtitle') || 'Instant download — arrangements, transcriptions & indexed books'}
                    count={sections.arrangements.length + sections.forscore.length}
                    testId="section-digital-products"
                  />

                  <div
                    data-testid="shop-my-downloads-banner"
                    className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6 bg-[#d4af37]/[0.06] border border-[#d4af37]/25 rounded-2xl px-5 py-4 -mt-4 mb-10"
                  >
                    <p className="text-sm text-neutral-300 flex-1">
                      Already purchased a digital file? Get fresh download links anytime — no account needed.
                    </p>
                    <Link
                      to="/my-downloads"
                      data-testid="shop-my-downloads-button"
                      className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full border border-[#d4af37]/60 text-[#d4af37] text-sm font-semibold hover:bg-[#d4af37] hover:text-black transition-colors whitespace-nowrap"
                    >
                      <Download className="w-4 h-4" />
                      My Downloads
                    </Link>
                  </div>

                  {/* Arrangements subsection */}
                  {sections.arrangements.length > 0 && (
                    <div ref={arrangementsRef}>
                      <SubSectionHeader
                        title={t('arrangements') || 'Arrangements & Transcriptions'}
                        count={sections.arrangements.length}
                        testId="subsection-arrangements"
                      />
                      <ProductGrid products={sections.arrangements} />
                    </div>
                  )}

                  {/* ForScore Indexed Files subsection */}
                  {sections.forscore.length > 0 && (
                    <div ref={forscoreRef}>
                      <SubSectionHeader
                        title={t('forscoreFiles') || 'ForScore Indexed Files'}
                        count={sections.forscore.length}
                        testId="subsection-forscore"
                      />
                      <div data-testid="shop-sample-links" className="flex flex-wrap items-center gap-x-4 gap-y-2 -mt-4 mb-8">
                        <span className="text-[#d4af37] text-xs font-semibold uppercase tracking-[0.14em]">
                          {sampleCopy(language).kicker}
                        </span>
                        <a
                          href={sampleUrlFor('forscore').url}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid="shop-sample-forscore"
                          className="inline-flex items-center gap-1.5 text-sm text-white hover:text-[#d4af37] underline decoration-[#d4af37]/40 transition-colors"
                        >
                          ⬇ forScore sample (.4sc)
                        </a>
                        <a
                          href={sampleUrlFor('mobilesheets').url}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid="shop-sample-mobilesheets"
                          className="inline-flex items-center gap-1.5 text-sm text-white hover:text-[#d4af37] underline decoration-[#d4af37]/40 transition-colors"
                        >
                          ⬇ MobileSheets sample (.msf)
                        </a>
                        <Link to="/forscore" className="text-neutral-500 hover:text-[#d4af37] text-xs transition-colors">
                          {sampleCopy(language).seeMore} →
                        </Link>
                      </div>
                      <ProductGrid products={sections.forscore} />
                    </div>
                  )}

                  {/* MobileSheets Indexed Files subsection */}
                  {sections.mobilesheets.length > 0 && (
                    <div ref={mobilesheetsRef}>
                      <SubSectionHeader
                        title="MobileSheets Indexed Files"
                        count={sections.mobilesheets.length}
                        testId="subsection-mobilesheets"
                      />
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 -mt-4 mb-8">
                        <a
                          href={sampleUrlFor('mobilesheets').url}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid="shop-sample-mobilesheets-section"
                          className="inline-flex items-center gap-1.5 text-sm text-white hover:text-emerald-400 underline decoration-emerald-400/40 transition-colors"
                        >
                          ⬇ MobileSheets sample (.msf)
                        </a>
                        <Link to="/mobilesheets" className="text-neutral-500 hover:text-emerald-400 text-xs transition-colors">
                          {sampleCopy(language).seeMore} →
                        </Link>
                      </div>
                      <ProductGrid products={sections.mobilesheets} />
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : null}
        </div>
      </section>
    </Layout>
  );
}

export default ShopPage;
